import { PREVIEW_TOKEN_HEADER } from './constants';
import { PLATFORM_DOMAIN, PLATFORM_SCHEME } from './env';

// The platform's own host labels, mirrored from the backend's hard-reserved
// list. The backend's soft list (book, booking, mokoboko, and the scheduler's
// page names) is deliberately not mirrored: those are refused when a new studio
// picks a slug, but a studio already holding one must stay reachable.
// Copied from mokoboko-backend src/utils/tenant-slug.ts PLATFORM_HOST_LABELS. A
// label on only one list is minted there and 404'd here, so change both together.
const RESERVED_SLUGS = new Set([
  'admin',
  'api',
  'app',
  'account',
  'assets',
  'auth',
  'billing',
  'blog',
  'cdn',
  'dev',
  'docs',
  'help',
  'login',
  'mail',
  'pay',
  'staging',
  'static',
  'status',
  'support',
  'test',
  'www',
]);

// 3-63 characters, lowercase alphanumerics and hyphens, never on an edge —
// anything else is not a legal DNS label and the backend will not resolve it.
const SLUG_REGEX = /^[a-z0-9](?:[a-z0-9-]{1,61}[a-z0-9])$/;

// The trailing dot of a fully qualified name is stripped here rather than at
// the call sites: `www.{PLATFORM_DOMAIN}.` is a legal Host, and left in place
// it fails the suffix test and walks the platform's own address past the
// reserved-host check.
export const hostnameOf = (host: string) =>
  host.toLowerCase().split(':')[0].replace(/\.$/, '');

const PLATFORM_HOSTNAME = hostnameOf(PLATFORM_DOMAIN);
const PLATFORM_SUFFIX = `.${PLATFORM_HOSTNAME}`;

// Three answers, not two. Null means the host is outside the platform domain —
// a studio's own address, which carries no slug because the backend resolves it
// from the Origin. An empty string means the platform apex: under the domain,
// but a shared address that cannot name a studio by itself. Anything else is
// the subdomain label.
const platformLabel = (host: string | null | undefined): string | null => {
  if (!host) return null;
  const hostname = hostnameOf(host);
  if (hostname === PLATFORM_HOSTNAME) return '';
  if (!hostname.endsWith(PLATFORM_SUFFIX)) return null;
  return hostname.slice(0, -PLATFORM_SUFFIX.length);
};

export const isTenantSlug = (label: string) =>
  SLUG_REGEX.test(label) && !RESERVED_SLUGS.has(label);

// Null is a normal answer, not a failure: a studio on its own domain has no
// slug in its host. Guessing one from the first label is what points a custom
// domain at whichever unrelated studio happens to hold that name.
export const tenantSlugFromHost = (
  host: string | null | undefined
): string | null => {
  const label = platformLabel(host);
  return label !== null && isTenantSlug(label) ? label : null;
};

// Shares isTenantSlug with the slug lookup so the two can never disagree: a
// reserved host is exactly one that is under the platform domain yet yields no
// usable slug. That covers the apex as well as www/api/admin — the apex is a
// shared address, so the backend answers TENANT_SLUG_REQUIRED for it and there
// is no studio to render. A host outside the platform domain is never reserved:
// `www.studio.com` is a studio's own www twin, which the backend registers
// alongside the apex on purpose.
export const isReservedPlatformHost = (
  host: string | null | undefined
): boolean => {
  const label = platformLabel(host);
  return label !== null && !isTenantSlug(label);
};

// The host a cached server answer is filed under. A studio's own www is folded
// onto its apex: the backend resolves the two as one studio, and its revalidate
// names only the apex, so one shared entry lets that one tag clear both twins.
// With two entries the www twin would keep a stale booking_host and indexable
// for up to 300 s after a save, publishing a different canonical and robots
// than the apex. Platform hosts are left as they are, `www.{PLATFORM_DOMAIN}`
// included, since that is the reserved www label.
export const cacheHostOf = (host: string) =>
  platformLabel(host) === null ? host.replace(/^www\./i, '') : host;

// The one string the backend CORS-matches on SSR, and the one published as the
// canonical URL — both must be built the same way.
export const platformOrigin = (host: string) => `${PLATFORM_SCHEME}://${host}`;

// The slug a TENANT_MOVED answer names, or null. It becomes a hostname below,
// so anything that is not a legal slug (`evil.com`, `../x`) is refused rather
// than trusted because the API said it.
export const movedTenantSlug = (details: unknown): string | null => {
  const slug = (details as { slug?: unknown } | null)?.slug;
  return typeof slug === 'string' && isTenantSlug(slug) ? slug : null;
};

// The host comes from the slug and the platform domain only, never from a
// string in the API body. `pathname` must already be a validated path.
export const movedStudioUrl = (
  slug: string,
  pathname: string,
  search: string
) => {
  const url = new URL(pathname, platformOrigin(`${slug}.${PLATFORM_DOMAIN}`));
  url.search = search;
  return url;
};

// The head script in app/layout.tsx strips claim, reset and preview tokens from
// the address bar before hydration, so in the browser the query this document
// was requested with survives only in its navigation entry. A forward must
// carry those tokens. The server has no navigation entry and is handed the
// requested query already.
export const requestedSearch = (search: string) => {
  const entry = performance.getEntriesByType('navigation')[0];
  return entry
    ? new URLSearchParams(new URL(entry.name).search).toString()
    : search;
};

// Two hosts that each hold a cached moved answer naming the other (a rename
// back whose revalidate never arrived) would forward a visitor between them
// without end. Arriving from the target is the sign.
export const isForwardLoop = (referrer: string, target: URL) =>
  referrer !== '' && new URL(referrer).origin === target.origin;

// A bare hostname, as the backend stores a studio's own domain: labels of
// letters, digits and hyphens, at least one dot, no scheme, port or path.
export const HOSTNAME =
  /^(?=.{1,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;

// booking_host, not custom_domain: a studio whose registrar forwards its apex
// to www is served on www only, so sending www to the apex would loop forever.
// The backend publishes whichever of the two is live. It becomes the Location
// of a redirect, so only a bare hostname outside the platform domain is taken;
// anything else leaves every host naming itself, as before the studio had a
// domain of its own.
const bookingHostOf = (bookingHost: string | null | undefined) =>
  bookingHost &&
  HOSTNAME.test(bookingHost) &&
  platformLabel(bookingHost) === null
    ? bookingHost
    : null;

// The request host itself when it already is the booking host, so the port it
// arrived on survives in the published URL.
const canonicalHost = (
  bookingHost: string | null | undefined,
  requestHost: string
) => {
  const target = bookingHostOf(bookingHost);
  return target === null || hostnameOf(requestHost) === target
    ? requestHost
    : target;
};

export const canonicalOrigin = (
  bookingHost: string | null | undefined,
  requestHost: string
) => platformOrigin(canonicalHost(bookingHost, requestHost));

export const isCanonicalHost = (
  bookingHost: string | null | undefined,
  requestHost: string
) => canonicalHost(bookingHost, requestHost) === requestHost;

// Never from an apex to its own www. Two hosts whose cached booking_host names
// the other would send a visitor back and forth until a cache expires, and
// only an apex and its www can do that, since booking_host is never under the
// platform domain. The Referer cannot detect it: a browser keeps the first one
// across a redirect chain. The apex serves the page with www as its canonical;
// when a registrar forwards the apex to www, the apex never reaches us anyway.
export const redirectsToBookingHost = (
  bookingHost: string | null | undefined,
  requestHost: string
) => {
  const target = canonicalHost(bookingHost, requestHost);
  return target !== requestHost && target !== `www.${hostnameOf(requestHost)}`;
};

// A single leading slash and nothing that can start a new authority or a query.
// `//host` and `/@host` both re-point a canonical at another domain once
// concatenated onto the origin.
export const canonicalPathname = (pathname: string | null): string =>
  pathname && /^\/(?![/\\@])[^?#\s]*$/.test(pathname) ? pathname : '/';

// Empty or one query string with no fragment and no whitespace, since it is
// appended to a Location the server answers with.
export const canonicalSearch = (search: string | null): string =>
  search && /^\?[^#\s]*$/.test(search) ? search : '';

// A server render sends no Origin of its own, so the address is sent as one:
// that is the only form a studio's own domain can be resolved by, since it has
// no slug for the frontend to send without guessing. The slug rides along for
// the platform-subdomain case; the backend reads the Origin first and only
// falls through to the header on a shared address.
//
// The preview token has to be passed in rather than read here: the browser
// keeps it in sessionStorage, which a server render cannot see, so it arrives
// on the request URL and only the middleware can pick it up.
//
// The visitor's address rides along so the backend's rate limit counts the
// visitor rather than this server, which would otherwise share one budget
// across every studio's page views. The backend believes it only beside the
// proxy key, and that key is set on the server HTTP client, never here: this
// module is bundled into the Edge middleware and the browser.
export const tenantRequestHeaders = (
  host: string,
  slug: string | null,
  previewToken?: string | null,
  clientIp?: string | null
) => ({
  Origin: platformOrigin(host),
  ...(slug && { 'X-Tenant-Slug': slug }),
  ...(previewToken && { [PREVIEW_TOKEN_HEADER]: previewToken }),
  ...(clientIp && { 'x-mb-client-ip': clientIp }),
});
